import { useCallback, useEffect, useRef } from "react";
import i18n from "i18next";
import { canEdit } from "@/lib/documentAccess";
import { useQueryClient } from "@tanstack/react-query";
import { useApiContext } from "@/contexts/ApiContext";
import { useEditorStore } from "@/store/editorStore";
import { useSyncStatusStore, type SyncState } from "@/store/syncStatusStore";
import type { AutoSaveStatus } from "@/hooks/useAutoSave";
import { writeBuffer } from "@/lib/sync/editorBuffer";
import { mergeOnRebase, signatures } from "@/lib/sync/rebaseMerge";
import { advanceLocalBase } from "@/lib/sync/adoptedBase";
import {
  syncDocument,
  reportSyncTelemetry,
  type SyncTelemetryEvent,
} from "@/lib/sync/syncClient";
import { api, type DocumentWithBlocks } from "@/lib/api";
import { logger } from "@/lib/logger";
import { VERSION_SAVED_EVENT, type VersionSavedDetail } from "@/lib/savedMoment/events";
// Diagnostic logger — feeds the math-editor DevTools panel's "logs"
// tab so a manual tester running with `lilia.math.devtools=1` can see
// the live sequence of sync ticks, 409s, and rebases.
import { log as devlog } from "@/components/math-editor/logger";

/** Worker cadence. Tight while a LaTeX/LML preview is open so the
 *  source pane stays live (this is what the old autosave's "realtime"
 *  500ms debounce was for); relaxed otherwise to keep idle load low. */
const REALTIME_SYNC_INTERVAL_MS = 500;
const IDLE_SYNC_INTERVAL_MS = 1500;
/** Surface "Syncing" only if a round-trip lingers past this — sub-second
 *  syncs stay calm (UX spec). */
const SYNCING_VISIBLE_AFTER_MS = 800;
/** Consecutive failures before the indicator escalates to "error". */
const MAX_FAILURES = 3;
/** Telemetry flush cadence. */
const TELEMETRY_FLUSH_MS = 5000;

/** Map the rich sync state onto the legacy union the existing
 *  TopBar / StatusBar indicators consume. */
const SYNC_TO_AUTOSAVE: Record<SyncState, AutoSaveStatus> = {
  upToDate: "saved",
  syncing: "saving",
  offline: "offline",
  error: "error",
};

export interface UseDocumentSyncResult {
  status: AutoSaveStatus;
  syncState: SyncState;
  lastSavedAt: Date | null;
  isDirty: boolean;
  resolveConflict: (choice: "keepMine" | "takeTheirs") => Promise<void>;
  /** ⌘S — cut a named DocumentVersion (a history checkpoint, not a save).
   *  Flushes pending edits first; a blank name lets the server name it. */
  createCheckpoint: (name?: string | null) => Promise<void>;
  /**
   * Push any pending local edits now and wait for the tick to finish.
   * Used before Flow → Block mode switch so Block refetches server truth.
   */
  flushNow: () => Promise<void>;
  /**
   * Replace the local editor + sync baseline with a fresh server document.
   * Used after Ask Lilia applies ops (or Undo), so the canvas picks up remote
   * block changes without a full page reload. Resets editVersion / syncedSeq
   * the same way "takeTheirs" does on conflict.
   */
  adoptServerDocument: (remote: DocumentWithBlocks) => void;
}

interface UseDocumentSyncOptions {
  documentId: string;
  document: DocumentWithBlocks | null | undefined;
  /** True when a LaTeX/LML preview is visible — tightens the sync
   *  cadence so the server-rendered source pane refreshes promptly. */
  realtime?: boolean;
  /** Fired after each successful sync — EditorPage refreshes the preview. */
  onSynced?: () => void;
  /**
   * When false, the sync loop is paused (Flow kept alive but hidden under
   * Block). Prevents a background Flow tick from overwriting Block edits.
   */
  enabled?: boolean;
}

/**
 * Continuous background sync for the Flow editor — replaces the
 * debounced `useAutoSave`. Every edit write-throughs to an IndexedDB
 * buffer; a background loop pushes the working copy to the API,
 * carrying the document's optimistic-concurrency version. Sync
 * completion updates only the sync-status store, never the ProseMirror
 * canvas — which is what removes the old `onSuccess` re-render
 * collision. See architecture/2026-05-21-flow-editor-save-model.md.
 */
export function useDocumentSync({
  documentId,
  document,
  realtime = false,
  onSynced,
  enabled = true,
}: UseDocumentSyncOptions): UseDocumentSyncResult {
  const { fetchWithAuth } = useApiContext();
  const queryClient = useQueryClient();
  /** Latest sync tick — assigned inside the loop effect for awaitable flush. */
  const tickRef = useRef<() => Promise<void>>(async () => {});
  const intervalMs = realtime ? REALTIME_SYNC_INTERVAL_MS : IDLE_SYNC_INTERVAL_MS;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  // Indicator state. EditorPage re-renders on these; the canvas never
  // subscribes here, so a sync never re-renders the editor.
  const syncState = useSyncStatusStore((s) => s.state);
  const lastSavedAt = useSyncStatusStore((s) => s.lastSyncedAt);
  const pendingCount = useSyncStatusStore((s) => s.pendingCount);

  // Worker state — refs, off the React render path.
  const versionRef = useRef(0);
  const syncedSeqRef = useRef(0);
  const syncedTitleRef = useRef<string | null>(null);
  const syncingRef = useRef(false);
  const failuresRef = useRef(0);
  const conflictDocRef = useRef<DocumentWithBlocks | null>(null);
  const offlineSinceRef = useRef<number | null>(null);
  const telemetryRef = useRef<SyncTelemetryEvent[]>([]);
  const seededIdRef = useRef<string | null>(null);
  // What each side looked like at the last sync — the base of the three-way
  // merge a rebase makes (see mergeOnRebase). Each in its own side's format.
  const localBaseRef = useRef<Map<string, string> | null>(null);
  const serverBaseRef = useRef<Map<string, string> | null>(null);
  const onSyncedRef = useRef(onSynced);
  onSyncedRef.current = onSynced;

  // ── Seed once per document ────────────────────────────────────────
  // Keyed on the document object but guarded by `seededIdRef`, so a
  // later query-cache refresh for the same id doesn't reset the worker.
  useEffect(() => {
    if (!document || document.id !== documentId) return;
    if (seededIdRef.current === documentId) return;
    seededIdRef.current = documentId;

    useSyncStatusStore.getState().reset();
    syncingRef.current = false;
    failuresRef.current = 0;
    conflictDocRef.current = null;
    offlineSinceRef.current = null;
    syncedSeqRef.current = 0; // initDocument resets editVersion to 0
    versionRef.current = document.version ?? 0;
    syncedTitleRef.current = document.title;
    serverBaseRef.current = signatures(document.blocks ?? []);
    // The local base is taken on the first idle tick, from the editor itself,
    // so it is in the editor's format — the loaded blocks are in the server's.
    localBaseRef.current = null;
    useSyncStatusStore.getState().patch({ version: document.version ?? 0, documentId });

    // Initial write-through — the buffer starts level with the server.
    void writeBuffer({
      documentId,
      title: document.title,
      blocks: document.blocks,
      version: document.version ?? 0,
      editSeq: 0,
      syncedSeq: 0,
      updatedAt: Date.now(),
    }).catch(() => {
      /* the buffer is a cache — a write failure is non-fatal */
    });
  }, [documentId, document]);

  // ── Write-through: every edit lands in the IndexedDB buffer ───────
  useEffect(() => {
    const unsub = useEditorStore.subscribe(
      (s) => ({ editVersion: s.editVersion, title: s.title }),
      (curr) => {
        const blocks = useEditorStore.getState().getBlocksForSave();
        void writeBuffer({
          documentId,
          title: curr.title,
          blocks,
          version: versionRef.current,
          editSeq: curr.editVersion,
          syncedSeq: syncedSeqRef.current,
          updatedAt: Date.now(),
        }).catch(() => {
          /* non-fatal — see above */
        });
        useSyncStatusStore.getState().patch({
          pendingCount: Math.max(0, curr.editVersion - syncedSeqRef.current),
        });
      },
      { equalityFn: (a, b) => a.editVersion === b.editVersion && a.title === b.title },
    );
    return unsub;
  }, [documentId]);

  // ── The background sync loop ──────────────────────────────────────
  useEffect(() => {
    let stopped = false;
    // The server refused this document's saves (403) or doesn't know it (404, as for an account it was
    // never shared with). No retry changes that: the loop stops asking (3 Nov 2026: a refused editor
    // sent a save every 1–2 s for minutes). The Save button still tries once.
    let refused = false;

    // When the server's version advanced, auto-rebase instead of
    // surfacing a conflict modal: fetch the server's truth, merge by
    // block id (local wins on collisions, server-only blocks added in)
    // and let the next tick push the merged state with the new
    // expectedVersion. The user never sees the sync halt. A genuinely
    // colliding same-block edit is logged to telemetry; we don't
    // demand a destructive keepMine/takeTheirs choice up front.
    const rebase = async () => {
      const startedAt = Date.now();
      devlog.warn("rebase", "409 conflict — fetching server doc to merge", {
        documentId,
        clientVersion: versionRef.current,
      });
      let serverDoc: DocumentWithBlocks | null = null;
      try {
        const res = await api.documents.get(documentId);
        serverDoc = res.document;
      } catch (e) {
        devlog.error("rebase", "server fetch failed — will retry on next tick", {
          error: e instanceof Error ? e.message : String(e),
        });
        return;
      }
      if (!serverDoc) {
        devlog.warn("rebase", "server returned no document — skipping");
        return;
      }
      const serverBlocks = serverDoc.blocks ?? [];
      const localBlocks = useEditorStore.getState().getBlocksForSave();
      const bases = localBaseRef.current && serverBaseRef.current
        ? { local: localBaseRef.current, server: serverBaseRef.current }
        : null;
      const { merged, inserted: serverOnly, removed, replaced, collisions } =
        mergeOnRebase(localBlocks, serverBlocks, bases);
      // The server's state is the new server base. The local base keeps what
      // this editor last pushed for the blocks it changed — and takes the
      // blocks this merge adopted from the server, which are unchanged here.
      // Left stale, an adopted block read as a local edit on the next 409 and
      // this editor's older copy overwrote another tab's newer one.
      serverBaseRef.current = signatures(serverBlocks);
      if (localBaseRef.current) {
        localBaseRef.current = advanceLocalBase(localBaseRef.current, [...replaced, ...serverOnly]);
      }
      // Bump editVersion so the next tick re-syncs the merged state.
      // Also queue the server-only blocks for surgical insertion into
      // the Flow canvas. BlockEditor drains the queue with
      // `insertContentAt` rather than a full `setContent` replace —
      // the latter would wipe any in-flight typing the user has done
      // since this rebase fetch started (REGRESSION-002).
      useEditorStore.setState((s) => ({
        blocks: merged,
        isDirty: true,
        editVersion: s.editVersion + 1,
        pendingRebaseInserts: [...s.pendingRebaseInserts, ...serverOnly],
        pendingRebaseRemovals: [...s.pendingRebaseRemovals, ...removed],
        pendingRebaseReplacements: [...s.pendingRebaseReplacements, ...replaced],
      }));
      if (collisions.length > 0) {
        telemetryRef.current.push({
          eventKind: "conflict",
          severity: "warn",
          documentId,
          detail: `Both sides changed ${collisions.length} block(s); kept this editor's version.`,
        });
      }
      versionRef.current = serverDoc.version ?? 0;
      useSyncStatusStore.getState().patch({ version: versionRef.current });
      devlog.info("rebase", "merge complete — next tick will push", {
        clientVersion: versionRef.current,
        localBlocks: localBlocks.length,
        serverOnlyAdded: serverOnly.length,
        removedRemotely: removed.length,
        replacedRemotely: replaced.length,
        collisions: collisions.length,
        mergedTotal: merged.length,
        elapsedMs: Date.now() - startedAt,
      });
      telemetryRef.current.push({
        eventKind: "conflict",
        severity: "info",
        documentId,
        detail: "Auto-rebased on server version advance.",
      });
    };

    const tick = async (opts?: { force?: boolean }) => {
      if (stopped || syncingRef.current) return;
      if (refused && !opts?.force) return;
      // Background interval pauses when Flow is hidden; flushNow always forces.
      if (!enabledRef.current && !opts?.force) return;
      // note: tickRef is assigned after tick is fully defined

      const store = useEditorStore.getState();
      if (store.documentId !== documentId) return;
      // A viewer has nothing to push — the API would refuse it (403), and the
      // bar would say "Couldn't save" about edits they were never meant to make.
      if (!canEdit(store.document as { role?: string } | null)) {
        useSyncStatusStore.getState().patch({ state: "upToDate", pendingCount: 0 });
        return;
      }

      if (!navigator.onLine) {
        if (offlineSinceRef.current == null) offlineSinceRef.current = Date.now();
        useSyncStatusStore.getState().patch({ state: "offline" });
        return;
      }

      const editVersion = store.editVersion;
      const title = store.title;
      const blocksChanged = editVersion > syncedSeqRef.current;
      const titleChanged = title !== syncedTitleRef.current;
      if (!blocksChanged && !titleChanged) {
        // Nothing typed since load: the editor still shows the server's state,
        // so this is the local base, in the editor's own format.
        if (localBaseRef.current === null && store.editorInstance && !store.editorInstance.isDestroyed) {
          localBaseRef.current = signatures(store.getBlocksForSave());
        }
        if (useSyncStatusStore.getState().state !== "error") {
          useSyncStatusStore.getState().patch({ state: "upToDate", pendingCount: 0 });
        }
        return;
      }

      syncingRef.current = true;
      // Show "Syncing" only if the round-trip lingers — sub-second
      // syncs stay calm (UX spec: don't flash on every keystroke).
      const syncingTimer = window.setTimeout(() => {
          useSyncStatusStore.getState().patch({ state: "syncing" });
      }, SYNCING_VISIBLE_AFTER_MS);

      try {
        if (blocksChanged) {
          const blocks = store.getBlocksForSave();
          const tickStartedAt = Date.now();
          devlog.info("sync-tick", `pushing ${blocks.length} blocks`, {
            documentId,
            expectedVersion: versionRef.current,
            editVersion,
          });
          const result = await syncDocument(fetchWithAuth, documentId, {
            blocks,
            expectedVersion: versionRef.current,
          });
          if (result.kind === "conflict") {
            devlog.warn("sync-tick", "→ 409 conflict", {
              expectedVersion: versionRef.current,
              roundTripMs: Date.now() - tickStartedAt,
            });
            await rebase();
            return; // the merged state will go out on the next tick
          }
          if (result.kind === "refused") {
            refused = true;
            devlog.warn("sync-tick", `→ ${result.status}, refused: the loop stops`, { documentId });
            useSyncStatusStore.getState().patch({
              state: "error",
              detail: i18n.t(result.status === 403 ? "editor:saveState.refusedEdit" : "editor:saveState.refusedGone"),
            });
            telemetryRef.current.push({
              eventKind: "sync_error",
              severity: "warn",
              documentId,
              detail: `HTTP ${result.status}: saves refused; sync stopped`,
            });
            return;
          }
          if (result.kind === "error") {
            devlog.error("sync-tick", "→ error", {
              message: result.message,
              roundTripMs: Date.now() - tickStartedAt,
            });
            throw new Error(result.message);
          }
          devlog.info("sync-tick", `→ ok, server version=${result.version}`, {
            previousVersion: versionRef.current,
            newVersion: result.version,
            roundTripMs: Date.now() - tickStartedAt,
          });
          versionRef.current = result.version;
          syncedSeqRef.current = editVersion;
          // Both sides now hold exactly this: the new base for the next rebase.
          localBaseRef.current = signatures(blocks);
          if (result.blocks.length > 0) serverBaseRef.current = signatures(result.blocks);
          useSyncStatusStore.getState().patch({ version: result.version });
        }
        if (titleChanged) {
          await fetchWithAuth(`/api/documents/${documentId}`, {
            method: "PUT",
            body: JSON.stringify({ title }),
          });
          syncedTitleRef.current = title;
        }

        failuresRef.current = 0;
        if (offlineSinceRef.current != null) {
          telemetryRef.current.push({
            eventKind: "offline_span",
            severity: "info",
            documentId,
            durationMs: Date.now() - offlineSinceRef.current,
          });
          offlineSinceRef.current = null;
        }
        useSyncStatusStore.getState().patch({
          state: "upToDate",
          lastSyncedAt: new Date(),
          pendingCount: Math.max(0, useEditorStore.getState().editVersion - syncedSeqRef.current),
          detail: null,
        });
        onSyncedRef.current?.();
      } catch (err) {
        failuresRef.current += 1;
        const message = err instanceof Error ? err.message : "Sync failed";
        if (!navigator.onLine) {
          if (offlineSinceRef.current == null) offlineSinceRef.current = Date.now();
          useSyncStatusStore.getState().patch({ state: "offline" });
          devlog.warn("sync", `network offline (attempt ${failuresRef.current})`);
        } else if (failuresRef.current >= MAX_FAILURES) {
          useSyncStatusStore.getState().patch({ state: "error", detail: message });
          devlog.error("sync", `retry exhausted after ${failuresRef.current} attempts`, { message });
          telemetryRef.current.push({
            eventKind: "retry_exhausted",
            severity: "error",
            documentId,
            attemptCount: failuresRef.current,
            detail: message,
          });
        } else {
          devlog.warn("sync", `sync failed (attempt ${failuresRef.current})`, { message });
        }
        // else: stay quiet and retry on the next tick.
        logger.debug(
          "Sync attempt failed",
          { documentId, message, attempt: failuresRef.current },
          "sync",
        );
      } finally {
        window.clearTimeout(syncingTimer);
        syncingRef.current = false;
      }
    };

    // Always keep an awaitable tick for mode-switch flush (even when hidden).
    tickRef.current = () => tick({ force: true });

    // Background loop only while Flow is the active surface.
    if (!enabled) {
      return () => {
        stopped = true;
      };
    }

    devlog.info("sync", "sync loop started", {
      documentId,
      intervalMs,
      maxFailures: MAX_FAILURES,
    });
    const interval = window.setInterval(() => void tick(), intervalMs);
    const onOnline = () => {
      devlog.info("sync", "network online — kicking a tick");
      void tick();
    };
    window.addEventListener("online", onOnline);
    // Manual Save button (TopBar) dispatches this event. Reuses the
    // same tick path — push pending blocks now, surface the in-flight
    // chip via the existing SYNCING_VISIBLE_AFTER_MS guard. Idempotent:
    // if nothing's changed since the last successful tick, this is a
    // no-op and the chip stays "up to date".
    const onSaveNow = () => {
      devlog.info("sync", "save-now event — kicking a tick");
      void tick({ force: true });
    };
    window.addEventListener("lilia:save-now", onSaveNow as EventListener);

    return () => {
      stopped = true;
      window.clearInterval(interval);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("lilia:save-now", onSaveNow as EventListener);
      devlog.info("sync", "sync loop stopped");
      // Do NOT invalidate document on pause (enabled→false) — that was
      // flashing a full loading state during Flow↔Block switches.
    };
  }, [documentId, fetchWithAuth, queryClient, intervalMs, enabled]);

  // ── Telemetry flush ───────────────────────────────────────────────
  useEffect(() => {
    const flush = () => {
      if (telemetryRef.current.length === 0) return;
      const batch = telemetryRef.current.splice(0, telemetryRef.current.length);
      void reportSyncTelemetry(fetchWithAuth, batch);
    };
    const interval = window.setInterval(flush, TELEMETRY_FLUSH_MS);
    return () => {
      window.clearInterval(interval);
      flush();
    };
  }, [fetchWithAuth]);

  // ── Conditional unload guard ──────────────────────────────────────
  // Warn only when there are at-risk pending writes — offline, error
  // or conflict. Stays silent when the sync is healthy or merely
  // mid-flight, so the "it just saves" promise still holds.
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      const s = useSyncStatusStore.getState();
      if (s.state === "offline" || s.state === "error") {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  const resolveConflict = useCallback(
    async (choice: "keepMine" | "takeTheirs") => {
      const remote = conflictDocRef.current;
      if (choice === "takeTheirs" && remote) {
        // Discard local edits — reload the editor with the server copy.
        useEditorStore.getState().initDocument(remote);
        versionRef.current = remote.version ?? 0;
        syncedSeqRef.current = 0; // initDocument reset editVersion to 0
        syncedTitleRef.current = remote.title;
        queryClient.setQueryData(["document", documentId], remote);
      } else if (remote) {
        // keepMine — adopt the server version so the next conditional
        // write succeeds, overwriting the server copy with local edits.
        versionRef.current = remote.version ?? 0;
      }
      conflictDocRef.current = null;
      failuresRef.current = 0;
      useSyncStatusStore.getState().patch({ state: "upToDate", detail: null });
    },
    [documentId, queryClient],
  );

  const adoptServerDocument = useCallback(
    (remote: DocumentWithBlocks) => {
      if (remote.id !== documentId) return;
      useEditorStore.getState().initDocument(remote);
      versionRef.current = remote.version ?? 0;
      syncedSeqRef.current = 0;
      syncedTitleRef.current = remote.title;
      queryClient.setQueryData(["document", documentId], remote);
      useSyncStatusStore.getState().patch({
        state: "upToDate",
        version: remote.version ?? 0,
        pendingCount: 0,
        detail: null,
      });
    },
    [documentId, queryClient],
  );

  /** Await one (or two) sync ticks so pending edits hit the server. */
  const flushNow = useCallback(async () => {
    const waitInFlight = async () => {
      const start = Date.now();
      while (syncingRef.current && Date.now() - start < 8000) {
        await new Promise((r) => setTimeout(r, 40));
      }
    };
    await waitInFlight();
    await tickRef.current();
    // Rebase path may leave work for the next tick — run once more.
    await waitInFlight();
    await tickRef.current();
  }, []);

  const createCheckpoint = useCallback(async (name?: string | null) => {
    try {
      // A version is a snapshot of what the server holds, so what the author
      // typed a moment ago has to be there first — or "Version saved" would
      // be saying it about the text before their last sentence.
      await flushNow();
      const response = await fetchWithAuth(`/api/documents/${documentId}/versions`, {
        method: "POST",
        body: JSON.stringify({ name: name?.trim() ? name.trim() : null }),
      });
      if (!response.ok) throw new Error(`Creating the version failed (${response.status})`);
      const version = (await response.json().catch(() => ({}))) as {
        name?: string | null;
        versionNumber?: number;
        createdAt?: string;
      };
      queryClient.invalidateQueries({ queryKey: ["document-versions", documentId] });
      // The saved moment's `version` state names it (saved-moment handoff §3).
      const detail: VersionSavedDetail = {
        documentId,
        name: version.name || (version.versionNumber ? `Version ${version.versionNumber}` : "Version"),
        createdAt: version.createdAt ?? new Date().toISOString(),
      };
      window.dispatchEvent(new CustomEvent<VersionSavedDetail>(VERSION_SAVED_EVENT, { detail }));
    } catch (err) {
      logger.error(
        "Checkpoint (named version) failed",
        { documentId, error: String(err) },
        "sync",
      );
    }
  }, [documentId, fetchWithAuth, queryClient, flushNow]);

  return {
    status: SYNC_TO_AUTOSAVE[syncState],
    syncState,
    lastSavedAt,
    isDirty: pendingCount > 0,
    resolveConflict,
    createCheckpoint,
    flushNow,
    adoptServerDocument,
  };
}
